import { useRef, useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Eraser, Check, Undo2 } from 'lucide-react';

const SignatureCanvas = ({ 
  onSignatureChange, 
  initialSignature = null,
  label = "Signature",
  readOnly = false,
  width = 300,
  height = 150
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // Load initial signature if provided
    if (initialSignature) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setHasSignature(true);
      };
      img.src = initialSignature;
    }
  }, [initialSignature]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches && e.touches[0]) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    if (readOnly) return;
    e.preventDefault();
    const coords = getCoordinates(e);
    setIsDrawing(true);
    setLastPosition(coords);
  };

  const draw = (e) => {
    if (!isDrawing || readOnly) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(lastPosition.x, lastPosition.y);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    
    setLastPosition(coords);
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      if (hasSignature) {
        const canvas = canvasRef.current;
        const signatureData = canvas.toDataURL('image/png');
        onSignatureChange(signatureData);
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-gray-500 text-sm font-medium">{label}</label>
        {!readOnly && hasSignature && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={clearSignature}
            className="text-gray-500 hover:text-[#181B1F] h-7 px-2"
          >
            <Eraser className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}
      </div>
      <div 
        className={`relative border-2 rounded-sm overflow-hidden ${
          readOnly ? 'border-gray-200 bg-gray-50' : 'border-gray-300 bg-white'
        }`}
        style={{ touchAction: 'none' }}
      >
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className={`w-full ${readOnly ? 'cursor-not-allowed' : 'cursor-crosshair'}`}
          style={{ height: 'auto', aspectRatio: `${width}/${height}` }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!hasSignature && !readOnly && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-neutral-400 text-sm">Sign here</p>
          </div>
        )}
        {hasSignature && (
          <div className="absolute bottom-1 right-1">
            <Check className="w-4 h-4 text-green-500" />
          </div>
        )}
      </div>
      {!readOnly && (
        <p className="text-gray-400 text-xs">
          Draw your signature using mouse or touch
        </p>
      )}
    </div>
  );
};

export default SignatureCanvas;
